import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {Row} from '@edna/components/primitives';
import {ECurrency, ECurrencySymbol, TCurrency, formatNumber} from '@edna/utils';

import {Link} from 'src/components/primitives';
import {EChannelType, isEnDomain} from 'src/constants';
import {EResellerIds} from 'src/containers/User';

import {showTariffCostModal} from './Modals';
import {
  TSmsTariffPayload,
  TTariffHistory,
  TTariffTemplate,
  TViberTariffPayload,
  TWhatsappTariffPayload,
} from './definitions';
import * as S from './style';

type TCommonProps = {
  tariff: TTariffTemplate | TTariffHistory;
  needAgreementConfirmation?: boolean;
  resellerId?: number | null;
  isTariff?: boolean;
  currency?: TCurrency;
};

type TProps = TCommonProps;
type TTariffDescriptionProps = TCommonProps & {
  tariff: TTariffTemplate | TTariffHistory;
  currency: TCurrency;
  hasPackagePrice?: boolean;
};

const SMS_PRICING_LINK_RU = 'https://edna.ru/sms_pricing_edna';
const SMS_PRICING_LINK_EUR = 'https://edna.io/pricing-sms-eur/';
const SMS_PRICING_LINK_USD = 'https://edna.io/pricing-sms-usd/';

const VIBER_PRICING_LINK_RU = 'https://edna.ru/viberbm_pricing/';

const WHATSAPP_PRICING_LINK_EUR = 'https://edna.io/pricing-whatsapp-cbp-eur/';
const WHATSAPP_PRICING_LINK_USD = 'https://edna.io/pricing-whatsapp-cbp-usd/';
const WHATSAPP_PRICING_LINK_IDR = 'https://edna.io/pricing-whatsapp-cbp-idr/';
const WHATSAPP_PRICING_LINK_KZ = 'https://edna.kz/kz/whatsapp_conv_pricing/';
const WHATSAPP_PRICING_LINK_RU = 'https://edna.ru/whatsapp_conv_pricing/';

const PackagePrice = React.memo(
  ({
    tariff,
    currency,
    needAgreementConfirmation,
  }: {
    tariff: TTariffTemplate<TViberTariffPayload>;
    currency: TCurrency;
    needAgreementConfirmation?: boolean;
  }) => {
    return (
      <>
        {needAgreementConfirmation && (
          <Trans
            i18nKey="LeadCompanyClientEdit:description.viber.packagePrice"
            values={{
              packagePrice: formatNumber(tariff.payload.packagePrice ?? 0),
              currency: ECurrencySymbol[currency],
            }}
          />
        )}
        {!needAgreementConfirmation && (
          <Trans i18nKey="LeadCompanyClientEdit:description.viber.packagePriceContract" />
        )}
      </>
    );
  },
);

const MFMResellerViberTariffDescription = React.memo(
  ({
    tariff,
    enViberPricingLink,
    currency,
    needAgreementConfirmation,
    hasPackagePrice,
  }: {
    tariff: TTariffTemplate<TViberTariffPayload>;
    enViberPricingLink: string;
    currency: TCurrency;
    needAgreementConfirmation?: boolean;
    hasPackagePrice?: boolean;
  }) => {
    return (
      <>
        {needAgreementConfirmation && (
          <>
            <Trans
              i18nKey="LeadCompanyClientEdit:description.viber.secondReseller.template_1_license"
              values={{
                licensePrice: formatNumber(tariff.payload.licensePrice ?? 0),
                currency: ECurrencySymbol[currency],
              }}
            />
            {hasPackagePrice && (
              <PackagePrice
                tariff={tariff}
                needAgreementConfirmation={needAgreementConfirmation}
                currency={currency}
              />
            )}
            <Trans i18nKey="LeadCompanyClientEdit:description.viber.secondReseller.template_1_link">
              <Link href={enViberPricingLink} />
            </Trans>
          </>
        )}
        {!needAgreementConfirmation && (
          <>
            <Trans i18nKey="LeadCompanyClientEdit:description.viber.secondReseller.template_1" />
            {hasPackagePrice && (
              <PackagePrice
                tariff={tariff}
                needAgreementConfirmation={needAgreementConfirmation}
                currency={currency}
              />
            )}
          </>
        )}
      </>
    );
  },
);

const ViberTariffTemplateDescription = React.memo(
  ({
    tariff,
    resellerId,
    currency,
    needAgreementConfirmation,
  }: TTariffDescriptionProps & {tariff: TTariffTemplate<TViberTariffPayload>}) => {
    const enViberPricingLink = [ECurrency.EUR, ECurrency.IDR].includes(currency as ECurrency)
      ? `https://edna.io/pricing-viberbm-${currency.toLowerCase()}/`
      : 'https://edna.io/pricing-viberbm-usd/';
    const isEnReseller =
      !!resellerId &&
      [EResellerIds.MFM_ID, EResellerIds.PT_ID, EResellerIds.KZ_ID].includes(resellerId);
    const hasPackagePrice = !!tariff?.payload?.packagePrice && isEnReseller;

    if (resellerId === EResellerIds.MFM_ID) {
      return (
        <MFMResellerViberTariffDescription
          tariff={tariff}
          enViberPricingLink={enViberPricingLink}
          currency={currency}
          needAgreementConfirmation={needAgreementConfirmation}
          hasPackagePrice={hasPackagePrice}
        />
      );
    }

    if (resellerId === EResellerIds.PT_ID) {
      return (
        <>
          {/* Оферта */}
          {needAgreementConfirmation && (
            <>
              <Trans
                i18nKey="LeadCompanyClientEdit:description.viber.thirdReseller.offer_1"
                values={{
                  licensePrice: formatNumber(tariff.payload.licensePrice ?? 0),
                  currency: ECurrencySymbol[currency],
                }}
              />

              {hasPackagePrice && (
                <Trans
                  i18nKey="LeadCompanyClientEdit:description.viber.thirdReseller.packagePrice"
                  values={{
                    packagePrice: formatNumber(tariff.payload.packagePrice ?? 0),
                    currency: ECurrencySymbol[currency],
                  }}
                />
              )}

              <Trans i18nKey="LeadCompanyClientEdit:description.viber.thirdReseller.offer_2">
                <Link href={enViberPricingLink} />
              </Trans>
              <Trans i18nKey="LeadCompanyClientEdit:description.viber.thirdReseller.offer_3" />
            </>
          )}
          {/* Договор */}
          {!needAgreementConfirmation && (
            <>
              <Trans i18nKey="LeadCompanyClientEdit:description.viber.thirdReseller.contract" />
              {hasPackagePrice && (
                <Trans i18nKey="LeadCompanyClientEdit:description.viber.thirdReseller.packagePriceContract" />
              )}
            </>
          )}
        </>
      );
    }

    return (
      <>
        <Trans
          i18nKey="LeadCompanyClientEdit:description.viber.firstReseller.template_1_1"
          values={{
            licensePrice: formatNumber(tariff.payload.licensePrice ?? 0),
            currency: ECurrencySymbol[currency],
          }}
        />
        {hasPackagePrice && (
          <PackagePrice
            tariff={tariff}
            currency={currency}
            needAgreementConfirmation={needAgreementConfirmation}
          />
        )}
        <Trans i18nKey="LeadCompanyClientEdit:description.viber.firstReseller.template_1_2" />
        {needAgreementConfirmation && (
          <>
            <Row>
              <Trans i18nKey="LeadCompanyClientEdit:description.viber.firstReseller.template_2_link">
                <Link href={isEnDomain() ? enViberPricingLink : VIBER_PRICING_LINK_RU} />
              </Trans>
            </Row>
          </>
        )}
        {!needAgreementConfirmation && (
          <>
            <Row>
              <Trans i18nKey="LeadCompanyClientEdit:description.viber.firstReseller.template_2" />
            </Row>
          </>
        )}
        <Trans i18nKey="LeadCompanyClientEdit:description.viber.firstReseller.template_3" />
      </>
    );
  },
);

const WhatsAppTariffTemplateDescription = React.memo(
  ({
    tariff,
    resellerId,
    needAgreementConfirmation,
    currency,
  }: TTariffDescriptionProps & {tariff: TTariffTemplate<TWhatsappTariffPayload>}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const showTariffTemplateData = React.useCallback(async () => {
      showTariffCostModal(
        tariff.payload.currency,
        tariff.payload.licenseCurrency,
        tariff.payload.dialogCurrency,
        tariff,
      );
    }, [dispatch, tariff]);

    const whatsappTariffLink = React.useMemo(() => {
      switch (resellerId) {
        case EResellerIds.MFM_ID:
        case EResellerIds.PT_ID:
          if (currency === ECurrency.EUR) {
            return WHATSAPP_PRICING_LINK_EUR;
          }

          if (currency === ECurrency.USD) {
            return WHATSAPP_PRICING_LINK_USD;
          }

          return WHATSAPP_PRICING_LINK_IDR;
        case EResellerIds.KZ_ID:
          return WHATSAPP_PRICING_LINK_KZ;
        default:
          return WHATSAPP_PRICING_LINK_RU;
      }
    }, [currency, resellerId]);

    if (resellerId === EResellerIds.PT_ID || resellerId === EResellerIds.MFM_ID) {
      return (
        <>
          {/* Оферта */}
          {needAgreementConfirmation && (
            <Trans
              i18nKey="LeadCompanyClientEdit:description.whatsapp.ioReseller.agreement"
              values={{
                licensePrice: formatNumber(tariff.payload.licensePrice ?? 0),
                period: t(`Tariffs:licensePeriod.${tariff.payload.licensePeriodMonths}`),
                currency: ECurrencySymbol[currency],
                mauCount: tariff.payload.mauCount,
                defaultMauPrice: formatNumber(tariff.payload.defaultMauPrice),
                price: formatNumber(tariff.payload.initPayment),
              }}
            >
              <Link href={whatsappTariffLink} />
            </Trans>
          )}
          {!needAgreementConfirmation &&
            t('LeadCompanyClientEdit:description.whatsapp.ioReseller.contract')}
        </>
      );
    }

    return (
      <>
        <Trans
          i18nKey="LeadCompanyClientEdit:description.whatsapp.firstReseller.template_1"
          values={{
            licensePrice: formatNumber(tariff.payload.licensePrice ?? 0),
            currency: ECurrencySymbol[currency],
            period: t(`Tariffs:licensePeriod.${tariff.payload.licensePeriodMonths}`),
            mauCount: tariff.payload.mauCount,
            defaultMauPrice: formatNumber(tariff.payload.defaultMauPrice),
            paymentCondition: tariff.payload.initPayment
              ? t('LeadCompanyClientEdit:description.whatsapp.firstReseller.paymentCondition', {
                  price: formatNumber(tariff.payload.initPayment),
                  currency: ECurrencySymbol[currency],
                })
              : t('LeadCompanyClientEdit:description.whatsapp.firstReseller.freePaymentCondition'),
          }}
        />
        {!needAgreementConfirmation && (
          <div>
            {t('LeadCompanyClientEdit:description.whatsapp.firstReseller.template_2_text_1')}
            <Link onClick={showTariffTemplateData}>
              {t('LeadCompanyClientEdit:description.whatsapp.firstReseller.template_2_text_2')}
            </Link>
          </div>
        )}
        {needAgreementConfirmation && (
          <Row>
            <Trans i18nKey="LeadCompanyClientEdit:description.whatsapp.firstReseller.template_2_link">
              <Link href={whatsappTariffLink} />
            </Trans>
          </Row>
        )}
        <Trans
          i18nKey={`LeadCompanyClientEdit:description.whatsapp.firstReseller.template_3${
            resellerId === EResellerIds.KZ_ID ? '_kzt' : ''
          }`}
        />
      </>
    );
  },
);

const SmsTariffTemplateDescription = React.memo(
  ({
    tariff,
    resellerId,
    needAgreementConfirmation,
    currency,
  }: TTariffDescriptionProps & {tariff: TTariffTemplate<TSmsTariffPayload>}) => {
    const {t} = useTranslation();
    const licensePrice = formatNumber(tariff.payload.licensePrice ?? 0);

    if (resellerId === EResellerIds.MFM_ID) {
      return (
        <>
          {needAgreementConfirmation && (
            <Trans
              i18nKey="LeadCompanyClientEdit:description.sms.secondReseller.template_1_link"
              values={{
                licensePrice,
                currency: ECurrencySymbol[currency],
              }}
            >
              <Link
                href={currency === ECurrency.EUR ? SMS_PRICING_LINK_EUR : SMS_PRICING_LINK_USD}
              />
            </Trans>
          )}
          {!needAgreementConfirmation && (
            <Trans i18nKey="LeadCompanyClientEdit:description.sms.secondReseller.template_1" />
          )}
        </>
      );
    }

    if (resellerId === EResellerIds.PT_ID) {
      return (
        <>
          {needAgreementConfirmation && (
            <>
              <Trans
                i18nKey="LeadCompanyClientEdit:description.sms.thirdReseller.offer_1"
                values={{
                  licensePrice,
                  currency: ECurrencySymbol[currency],
                }}
              />
              <Trans
                i18nKey="LeadCompanyClientEdit:description.sms.thirdReseller.offer_2"
                values={{
                  licensePrice,
                  currency: ECurrencySymbol[currency],
                }}
              >
                <Link
                  href={currency === ECurrency.EUR ? SMS_PRICING_LINK_EUR : SMS_PRICING_LINK_USD}
                />
              </Trans>
              <>{t('LeadCompanyClientEdit:description.viber.thirdReseller.offer_3')}</>
            </>
          )}
          {/* Договор */}
          {!needAgreementConfirmation && (
            <>{t('LeadCompanyClientEdit:description.sms.thirdReseller.contract')}</>
          )}
        </>
      );
    }

    return (
      <>
        <Trans
          i18nKey="LeadCompanyClientEdit:description.sms.firstReseller.template_1"
          values={{
            licensePrice,
            currency: ECurrencySymbol[currency],
          }}
        />
        {needAgreementConfirmation && (
          <Row>
            <Trans i18nKey="LeadCompanyClientEdit:description.sms.firstReseller.template_2_link">
              <Link href={SMS_PRICING_LINK_RU} />
            </Trans>
          </Row>
        )}
        {!needAgreementConfirmation && (
          <Row>
            <Trans i18nKey="LeadCompanyClientEdit:description.sms.firstReseller.template_2" />
          </Row>
        )}
        <Trans i18nKey="LeadCompanyClientEdit:description.sms.firstReseller.template_3" />
      </>
    );
  },
);

export const TariffTemplateDescription = React.memo<TProps>(
  ({
    tariff,
    needAgreementConfirmation,
    resellerId: resellerIdProp,
    isTariff,
    currency = ECurrency.RUB,
  }) => {
    const resellerId = resellerIdProp ?? tariff.resellerId;
    const channelType = tariff?.channelType;
    const {t} = useTranslation();

    if (
      tariff.currency === ECurrency.BYN ||
      [EResellerIds.BLR_FIRST_ID, EResellerIds.BLR_SECOND_ID].includes(resellerId as EResellerIds)
    ) {
      return <S.Description>{t('LeadCompanyClientEdit:description.blrTariff')}</S.Description>;
    }

    switch (channelType) {
      case EChannelType.WHATSAPP: {
        return (
          <WhatsAppTariffTemplateDescription
            tariff={tariff as TTariffTemplate<TWhatsappTariffPayload>}
            needAgreementConfirmation={needAgreementConfirmation}
            resellerId={resellerId}
            isTariff={isTariff}
            currency={currency}
          />
        );
      }

      case EChannelType.VIBER: {
        return (
          <ViberTariffTemplateDescription
            needAgreementConfirmation={needAgreementConfirmation}
            currency={currency}
            tariff={tariff as TTariffTemplate<TViberTariffPayload>}
            resellerId={resellerId}
          />
        );
      }

      case EChannelType.SMS: {
        return (
          <SmsTariffTemplateDescription
            needAgreementConfirmation={needAgreementConfirmation}
            currency={currency}
            tariff={tariff as TTariffTemplate<TSmsTariffPayload>}
            resellerId={resellerId}
          />
        );
      }

      default:
        return t('LeadCompanyClients:personalTariff');
    }
  },
);
